import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import supabase from "../../utils/supabase";
import { TIER_LABELS, LANG_LABELS } from "./pe-serving/shared/chassis";
import DeploymentPicker, {
  defaultDeployment,
  type DeploymentSelection,
} from "../sections/teacher/DeploymentPicker";
import type { Tier } from "../../engine/sequenceEngine";
import { peSeasonLabel } from "../../data/peSeasons";

const API = (import.meta as any).env?.VITE_API_URL ?? "https://pe.startupsmartup.com/api/v1";

interface Season { id: number; season_number: number; title: string; }
interface Chapter { id: number; chapter_number: number; title: string; }

// Surface = which PE learning surface this Canvas assignment launches into.
// Scenes -> /lti/scenes (reading, ungraded: no AGS lineItem).
// Formative -> /lti/assessment (graded: AGS lineItem with points_possible).
const SURFACES = [
  { value: "scenes", label: "Scenes (Story Reader)", blurb: "Story scenes — ungraded, no gradebook column." },
  { value: "assessment", label: "Formative Assessment", blurb: "Formative items — graded, passes back to the gradebook" },
  { value: "quiz", label: "Chapter Quiz", blurb: "Summative chapter quiz (Tier 3 or 4) — graded, passes back to the gradebook." },
] as const;
type Surface = (typeof SURFACES)[number]["value"];

export default function LtiChapterPickerPage() {
  const [searchParams] = useSearchParams();
  const ltiToken = searchParams.get("ltik") ?? localStorage.getItem("ltiToken") ?? "";

  // Canvas placement that opened this deep link (passed through by the backend
  // onDeepLinking handler). link_selection (Modules → + External Tool) is the
  // ungraded path and offers ONLY Scenes; assignment_selection (+ Assignment)
  // offers Scenes (ungraded lesson loop, default) + the graded surfaces
  // (Formative + Chapter Quiz). Scenes is FIRST so the loop — the primary
  // product — is the default selected surface. When the placement is unknown
  // (older launches / course_navigation) we fall back to offering all surfaces.
  const placement = searchParams.get("placement");
  const availableSurfaces =
    placement === "link_selection"
      ? SURFACES.filter((s) => s.value === "scenes")
      : placement === "assignment_selection"
        ? SURFACES.filter(
            (s) =>
              s.value === "scenes" ||
              s.value === "assessment" ||
              s.value === "quiz",
          )
        : SURFACES;
  const defaultSurface = availableSurfaces[0]?.value ?? "assessment";

  const [seasons, setSeasons] = useState<Season[]>([]);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<number | "">("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    chapter_id: "" as string,
    surface: defaultSurface as Surface,
    tier_override_mode: "use_student_default",
    tier_locked_to: "T1",
    language_override_mode: "use_student_default",
    language_locked_to: "en",
  });

  // The DeploymentPicker is path-agnostic (it's the SAME component the native
  // CreatePeAssignmentModal mounts) and needs ONE concrete tier to gate the
  // Assess (summative T3/T4) phase. When the teacher locks a tier, use it. When
  // tier is adaptive (use_student_default), use T4 so Assess is selectable and
  // defaults on — the per-student engine still tier-gates summative_quiz via
  // CANONICAL_STEPS, so a T1/T2 student never sees it regardless.
  const pickerTier = (form.tier_override_mode === "tier_locked"
    ? form.tier_locked_to
    : "T4") as Tier;
  const [deployment, setDeployment] = useState<DeploymentSelection>(() =>
    defaultDeployment("T4"),
  );

  // Asset narrowing only takes effect on the lesson-loop surface (/lti/scenes).
  // Formative/quiz are single-surface launches whose student pages ignore
  // `assets`, so the picker is shown — and `assets` sent — ONLY for scenes.
  const showDeployment = form.surface === "scenes";

  useEffect(() => {
    supabase.from("pe_seasons").select("id,season_number,title").order("season_number")
      .then(({ data }) => setSeasons(data ?? []));
  }, []);

  useEffect(() => {
    if (!selectedSeason) { setChapters([]); return; }
    supabase.from("pe_chapters").select("id,chapter_number,title").eq("season_id", selectedSeason).order("chapter_number")
      .then(({ data }) => setChapters(data ?? []));
  }, [selectedSeason]);

  // Chapter quizzes are summative T3/T4 packs only — a student's adaptive default
  // can land on T1/T2 (no quiz), so a quiz assignment is always tier-locked to
  // T3 or T4. Forcing it here keeps the picker from submitting an unservable
  // T1/T2 / adaptive quiz; the Locked-Tier control greys out T1/T2 (see below).
  useEffect(() => {
    if (form.surface === "quiz") {
      setForm((f) => ({
        ...f,
        tier_override_mode: "tier_locked",
        tier_locked_to: f.tier_locked_to === "T3" || f.tier_locked_to === "T4" ? f.tier_locked_to : "T3",
      }));
    }
  }, [form.surface]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.chapter_id) { setError("Select a chapter."); return; }
    // Empty-state guard — mirrors the native modal + engine EmptySequenceError.
    // Only applies on the loop surface, where the picker is shown and assets matter.
    if (showDeployment && deployment.assets.length === 0) {
      setError("Select at least one part to assign to students.");
      return;
    }
    setSubmitting(true); setError(null);

    const chapter = chapters.find(c => String(c.id) === form.chapter_id);
    const season = seasons.find(s => s.id === Number(selectedSeason));
    const surfaceLabel = SURFACES.find(s => s.value === form.surface)?.label ?? "Formative Assessment";
    const tierSuffix = form.tier_override_mode === "tier_locked" ? form.tier_locked_to : "Adaptive";
    const title = season && chapter
      ? `PE S${season.season_number}CH${String(chapter.chapter_number).padStart(2,"0")}: ${chapter.title} — ${surfaceLabel} (${tierSuffix})`
      : "Project Explore Chapter";

    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(`${API}/lti/submit-resources`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token ?? ""}` },
      body: JSON.stringify({
        selectedResources: {
          chapter_id: Number(form.chapter_id),
          surface: form.surface,
          tier_override_mode: form.tier_override_mode,
          tier_locked_to: form.tier_locked_to,
          language_override_mode: form.language_override_mode,
          language_locked_to: form.language_locked_to,
          title,
          // Deployment emit ({ presetId, assets }) — the SAME object shape native
          // stores in assignments.deployment. The backend packs it into the
          // launch custom.value query string; the student surface unpacks it
          // through the SAME parseDeployment(). Absent ⇒ full CANONICAL_STEPS.
          // Sent ONLY for the loop surface — undefined on formative/quiz drops
          // the key from JSON, so the box guard treats it as absent and their
          // custom.value gets no &assets append (those pages ignore it anyway).
          assets: showDeployment ? deployment : undefined,
        },
        ltik: ltiToken,
      }),
    });

    if (!res.ok) {
      setError("Failed to submit chapter selection.");
      setSubmitting(false);
      return;
    }

    // The response contains an HTML form that posts back to Canvas
    const text = await res.text();
    let html = text;
    try {
      const json = JSON.parse(text);
      if (json.deepLinkingResponse) html = json.deepLinkingResponse;
    } catch {}
    document.open(); document.write(html); document.close();
  };

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center pt-12 px-4">
      <div className="bg-white rounded-lg shadow-md p-8 w-full max-w-xl">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Select a PE Chapter</h1>
          <p className="text-gray-500 text-sm mt-1">Choose the chapter, surface, and settings for this Canvas assignment.</p>
        </div>

        {error && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Season</label>
              <select value={selectedSeason} onChange={e => setSelectedSeason(e.target.value ? Number(e.target.value) : "")}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                <option value="">Select season…</option>
                {/* Run E2 J3: canon season name by number. The old pe_seasons.title already held "Season N: ...", so the label showed "Season" twice. */}
                {seasons.map(s => <option key={s.id} value={s.id}>{peSeasonLabel(s.season_number)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Chapter</label>
              <select value={form.chapter_id} onChange={e => set("chapter_id", e.target.value)} required
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                <option value="">Select chapter…</option>
                {chapters.map(c => <option key={c.id} value={c.id}>CH{String(c.chapter_number).padStart(2,"0")}: {c.title}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Surface</label>
            <select value={form.surface} onChange={e => set("surface", e.target.value)}
              disabled={availableSurfaces.length <= 1}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-600">
              {availableSurfaces.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <p className="text-xs text-gray-400 mt-1">{availableSurfaces.find(s => s.value === form.surface)?.blurb}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Tier Setting</label>
              <select value={form.tier_override_mode} onChange={e => set("tier_override_mode", e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                <option
                  value="use_student_default"
                  disabled={form.surface === "quiz"}
                  title={form.surface === "quiz" ? "Quizzes must be locked to Tier 3 or 4." : undefined}
                >
                  Student's default tier
                </option>
                <option value="tier_locked">Lock all students to one tier</option>
              </select>
            </div>
            {form.tier_override_mode === "tier_locked" && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Locked Tier</label>
                <select value={form.tier_locked_to} onChange={e => set("tier_locked_to", e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                  {(["T1","T2","T3","T4"] as const).map(t => {
                    // Quizzes are summative T3/T4 packs only. For a quiz, T1/T2
                    // stay VISIBLE but disabled (greyed) with a tooltip — not
                    // hidden — so it's clear quizzes start at Tier 3.
                    const quizLocked = form.surface === "quiz" && (t === "T1" || t === "T2");
                    return (
                      <option
                        key={t}
                        value={t}
                        disabled={quizLocked}
                        title={quizLocked ? "Quizzes available for Tiers 3–4." : undefined}
                      >
                        {(TIER_LABELS as any)[t]}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Language Setting</label>
              <select value={form.language_override_mode} onChange={e => set("language_override_mode", e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                <option value="use_student_default">Student's preferred language</option>
                <option value="language_locked">Lock all students to one language</option>
              </select>
            </div>
            {form.language_override_mode === "language_locked" && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Locked Language</label>
                <select value={form.language_locked_to} onChange={e => set("language_locked_to", e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                  {(["en","es"] as const).map(l => <option key={l} value={l}>{(LANG_LABELS as any)[l]}</option>)}
                </select>
              </div>
            )}
          </div>

          {/* Deployment picker — what ships to students (spec §5). Same
              path-agnostic component the native modal mounts. Only the loop
              surface (/lti/scenes) consumes `assets`; on formative/quiz the
              toggles would no-op, so the panel is hidden there. */}
          {showDeployment && (
            <DeploymentPicker
              tier={pickerTier}
              value={deployment}
              onChange={setDeployment}
              disabled={submitting}
            />
          )}

          <button type="submit" disabled={submitting || !form.chapter_id || (showDeployment && deployment.assets.length === 0)}
            className="w-full py-2.5 bg-blue-600 text-white rounded font-medium hover:bg-blue-700 disabled:opacity-60 transition-colors">
            {submitting ? "Adding to Canvas…" : "Add to Canvas Assignment"}
          </button>
        </form>
      </div>
    </div>
  );
}
